import React, { useState } from "react";
import { RefreshCw, CheckCircle2, XCircle, MinusCircle } from "lucide-react";

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" });
}

function runOutcome(r) {
  if (r.fetch_status === "ok" && r.signals_emitted > 0) return "success";
  if (r.fetch_status === "ok") return "empty";
  return "failed";
}

function OutcomeBadge({ outcome }) {
  if (outcome === "success")
    return <span className="label-mono text-bm-green flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />SUCCESS</span>;
  if (outcome === "empty")
    return <span className="label-mono text-bm-amber flex items-center gap-1"><MinusCircle className="w-3 h-3" />EMPTY</span>;
  return <span className="label-mono text-bm-danger flex items-center gap-1"><XCircle className="w-3 h-3" />FAILED</span>;
}

export const RunLogs = ({ runs, onRefresh }) => {
  const [outcomeFilter, setOutcomeFilter] = useState("all");
  const counts = { success: 0, empty: 0, failed: 0 };
  runs.forEach((r) => { counts[runOutcome(r)] += 1; });
  const filtered = outcomeFilter === "all" ? runs : runs.filter((r) => runOutcome(r) === outcomeFilter);

  return (
    <div className="space-y-4" data-testid="logs-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1" data-testid="logs-outcome-filter">
          {[
            ["all", `ALL · ${runs.length}`],
            ["success", `SUCCESS · ${counts.success}`],
            ["empty", `EMPTY · ${counts.empty}`],
            ["failed", `FAILED · ${counts.failed}`],
          ].map(([v, label]) => (
            <button key={v} onClick={() => setOutcomeFilter(v)} data-testid={`logs-filter-${v}`}
              className={`px-2 py-1 text-xs font-mono border ${outcomeFilter === v
                ? "bg-bm-amber text-black border-bm-amber"
                : "border-white/10 text-bm-muted hover:text-white hover:border-white/30"}`}>
              {label}
            </button>
          ))}
        </div>
        <button onClick={onRefresh} data-testid="logs-refresh-btn"
          className="ml-auto flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono border border-bm-cyan/40 text-bm-cyan hover:bg-bm-cyan hover:text-black transition-colors">
          <RefreshCw className="w-3 h-3" />REFRESH
        </button>
      </div>

      <div className="bm-card overflow-hidden" data-testid="logs-table">
        <div className="hidden md:grid grid-cols-[140px_140px_90px_90px_110px_70px_1fr] gap-3 px-4 py-2 border-b border-white/10 label-mono text-bm-dim">
          <span>FINISHED</span><span>CHANNEL</span><span>TRIGGER</span><span>VIA</span><span>STATUS</span><span>SIGNALS</span><span>ERROR</span>
        </div>
        <div className="max-h-[560px] overflow-y-auto">
          {filtered.length === 0 && (
            <div className="px-4 py-6 font-mono text-xs text-bm-muted">No runs match this filter.</div>
          )}
          {filtered.map((r, i) => {
            const outcome = runOutcome(r);
            return (
              <div key={`${r.finished_at || ""}-${r.adapter}-${i}`}
                data-testid={`log-row-${r.adapter}-${i}`}
                className="grid grid-cols-1 md:grid-cols-[140px_140px_90px_90px_110px_70px_1fr] gap-1 md:gap-3 px-4 py-2 border-b border-white/5 last:border-b-0 font-mono text-xs items-start hover:bg-white/[0.02]">
                <span className="text-bm-dim">{fmtTime(r.finished_at)}</span>
                <span className="text-white">{r.name || r.adapter}</span>
                <span className="text-bm-dim uppercase">{r.trigger || "—"}</span>
                <span className="text-bm-cyan">{r.via || "—"}</span>
                <OutcomeBadge outcome={outcome} />
                <span className={r.signals_emitted > 0 ? "text-bm-cyan" : "text-bm-dim"}>{r.signals_emitted}</span>
                <span className={`text-[11px] leading-relaxed break-words ${outcome === "failed" ? "text-bm-danger" : "text-bm-muted"}`}>
                  {r.error || (outcome === "empty" ? "fetch ok — page yielded no parseable signals" : "ok")}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
